import {
  Link,
  ProgressBar,
  TableToolbar,
  Table,
  Typography,
} from 'melp-design/components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/material';
import { APP, ROUTES } from 'config';
import { formatCurrency } from 'utils/format';
import { COLOR_BY_BENEFIT_STATUS, benefitStatuses } from 'store/benefits';
import {
  EmployeeWalletBenefitsFilters,
  useEmployeeWalletBenefits,
} from 'store/employees';
import { useDefaultCurrency, useMe } from 'state/Administrators';
import { useRouteParams } from 'utils/useIdParam';
import {
  BenefitsFilter,
  FilterButton,
  Filters,
  SelectFilter,
} from 'components/filters';
import { COLOR_BY_STATUS } from 'store/assignments';

const INITIAL_FILTERS: EmployeeWalletBenefitsFilters = {
  page: 1,
  pageSize: APP.pagination.defaults.pageSize[0],
  status: [],
  benefits: [],
};

export const EmployeeWalletBenefits = () => {
  const { t, i18n } = useTranslation();
  const { id: employeeId } = useRouteParams(ROUTES.employees.details);
  const { hasViewPermission } = useMe();
  const { currency } = useDefaultCurrency();

  const [filters, setFilters] =
    useState<EmployeeWalletBenefitsFilters>(INITIAL_FILTERS);

  const { data: benefits, isLoading } = useEmployeeWalletBenefits(
    employeeId,
    filters,
  );

  return (
    <>
      <TableToolbar>
        <Stack direction="row" justifyContent="end" gap="10px">
          <Filters
            value={filters}
            onChange={setFilters}
            initialValues={INITIAL_FILTERS}
          >
            {({ value, initialValues, setFilter }) => (
              <>
                {hasViewPermission(['benefits']) ? (
                  <FilterButton
                    label={t('common.plan_name')}
                    value={value.benefits}
                    initialValue={initialValues.benefits}
                    onChange={(v) => setFilter('benefits', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <BenefitsFilter
                        value={{ value }}
                        onApplyFilter={({ value }) => applyFilter(value)}
                        onClearFilter={clearFilter}
                        onCancel={close}
                      />
                    )}
                  </FilterButton>
                ) : null}

                <FilterButton
                  label={t('common.current_status')}
                  value={value.status}
                  initialValue={initialValues.status}
                  onChange={(v) => setFilter('status', v)}
                >
                  {({ value, applyFilter, clearFilter, close }) => (
                    <SelectFilter
                      value={{ value }}
                      onApplyFilter={({ value }) => applyFilter(value)}
                      onClearFilter={clearFilter}
                      onCancel={close}
                      multiple
                      options={benefitStatuses.map((status) => ({
                        key: status,
                        label: t(`status.${status}`),
                      }))}
                    />
                  )}
                </FilterButton>
              </>
            )}
          </Filters>
        </Stack>
      </TableToolbar>

      <Table
        data={benefits?.items ?? []}
        isLoading={isLoading}
        columns={[
          {
            id: 'status',
            header: t('wallet.employeeWalletBenefitStatus'),
            cell: ({ row: { original: benefit } }) => (
              <Typography color={COLOR_BY_BENEFIT_STATUS[benefit.status]}>
                {t(`status.${benefit.status}`)}
              </Typography>
            ),
          },
          {
            id: 'assignmentStatus',
            header: t('alternatives.assignmentStatus'),
            cell: ({ row: { original: benefit } }) => (
              <Typography color={COLOR_BY_STATUS[benefit.assignmentStatus]}>
                {t(`status.${benefit.assignmentStatus}`)}
              </Typography>
            ),
          },
          {
            id: 'name',
            header: t('common.plan_name'),
            cell: ({ row: { original: benefit } }) =>
              hasViewPermission(['benefits']) ? (
                <Link
                  label={benefit.name}
                  to={ROUTES.benefits.details.replace(':id', benefit.id)}
                />
              ) : (
                benefit.name
              ),
          },
          {
            id: 'investment',
            header: t('benefitExpenses.benefitValue'),
            meta: { align: 'right' },
            cell: ({ row: { original: benefit } }) =>
              formatCurrency(i18n.language, benefit.investment, currency),
          },
          {
            id: 'expenses',
            header: t('employees.reserved'),
            meta: { align: 'right' },
            cell: ({ row: { original: benefit } }) =>
              formatCurrency(i18n.language, benefit.expenses, currency),
          },
          {
            id: 'percentage',
            cell: ({ row: { original: benefit } }) => {
              const percentage = benefit.investment
                ? (100 * benefit.expenses) / benefit.investment
                : 0;
              return (
                <ProgressBar
                  sx={{ width: 75 }}
                  value={percentage}
                  color="success"
                />
              );
            },
          },
          {
            id: 'remaining',
            header: t('benefitExpenses.remaining'),
            meta: { align: 'right' },
            cell: ({ row: { original: benefit } }) =>
              formatCurrency(i18n.language, benefit.remaining, currency),
          },
        ]}
        rows={{
          inactive: ({ original: benefit }) =>
            ['inactive', 'expired', 'draft'].includes(benefit.status),
        }}
        pagination={
          benefits && {
            count: benefits.total,
            page: benefits.page,
            pageSize: benefits.pageSize,
            pageSizeOptions: APP.pagination.defaults.pageSize,
            onChange: (page, pageSize) => {
              setFilters((prev) => ({ ...prev, page, pageSize }));
            },
          }
        }
      />
    </>
  );
};
